import React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { diContainer, serviceNames } from '../../diContainer/diContainer';
import withTranslations from '../../hocs/withTranslations';
import { ITranslations } from '../../interfaces/ITranslationFields';
import CartItem from '../../domain/entities/Cart/CartItem';
import Section from '../../screens/ScreenLayout/Section';
import ShoppingBagViewController from '../../controllers/ShoppingBagViewController';
import ShoppingBagHeader from './ShoppingBagHeader';
import ExpandedShoppingBag from './ExpandedShoppingBag';
import CollapsedShoppingBag from './CollapsedShoppingBag';
import { IGetPriceOptions } from '../../controllers/OrderSummaryViewController/OrderSummaryViewController';

const ShoppingBagWrapper = styled.div`
  display: flex;
  gap: 20px;
  flex-direction: column;
`;

interface IShoppingBagProps {
  cartItems: CartItem[];
  enableSplits?: boolean;
  getPrice(price: number, options?: IGetPriceOptions): string;
  hideHeader?: boolean;
  itemNotAvailableForPickUp?: boolean;
  translations?: ITranslations;
}

const ShoppingBagSection = styled(Section)``;

@observer
class ShoppingBag extends React.Component<IShoppingBagProps> {
  private readonly ctrl: ShoppingBagViewController;

  constructor(props: IShoppingBagProps) {
    super(props);
    this.ctrl = diContainer.get(serviceNames.shoppingBagViewController);
  }

  componentDidMount = (): void => {
    this.ctrl.loaded();
  };

  toggleShoppingBagOpenClose = (e: React.SyntheticEvent): void => {
    const { toggleShoppingBagOpen } = this.ctrl;
    e.stopPropagation();
    toggleShoppingBagOpen();
  };

  render(): JSX.Element {
    const {
      translations: {
        shoppingBag,
        standardShippingMessage,
      },
      cartItems,
      enableSplits,
      getPrice,
      hideHeader,
      itemNotAvailableForPickUp,
    } = this.props;

    const {
      data: {
        baseUrl,
        cartUrl,
        defaultShippingDaysTimeValue,
        showSplitShipments,
        isShoppingBagOpen,
        locale,
        showFreeLabelAsPrice,
        isBopisOrCourier,
        shipGroupedCartItems,
        orderSummary,
      },
    } = this.ctrl;

    let defaultShippingDays = '';
    if (showSplitShipments) {
      defaultShippingDays = standardShippingMessage.replace(
        '::DAYS::',
        `${defaultShippingDaysTimeValue}`,
      );
    }

    return (
      <ShoppingBagSection className="shopping-bag-wrapper">
        <ShoppingBagWrapper className="acac-shopping-bag-wrapper">
          {!hideHeader && (
            <ShoppingBagHeader
              title={shoppingBag}
              itemsCount={orderSummary.itemsCount}
              isCollapsed={!isShoppingBagOpen}
              toggleShoppingBagOpen={this.toggleShoppingBagOpenClose}
              cartUrl={cartUrl}
            />
          )}
          {isShoppingBagOpen
            ? (
              <ExpandedShoppingBag
                baseUrl={baseUrl}
                cartItems={cartItems}
                defaultShippingDays={defaultShippingDays}
                enableSplits={enableSplits || showSplitShipments}
                getPrice={getPrice}
                isBopisOrCourier={isBopisOrCourier}
                itemNotAvailableForPickUp={itemNotAvailableForPickUp}
                locale={locale}
                shipGroupedCartItems={shipGroupedCartItems}
                showFreeLabelAsPrice={showFreeLabelAsPrice}
              />
            )
            : (
              <CollapsedShoppingBag
                baseUrl={baseUrl}
                cartItems={cartItems}
                toggleShoppingBagOpen={this.toggleShoppingBagOpenClose}
              />
            )
          }
        </ShoppingBagWrapper>
      </ShoppingBagSection>
    );
  }
}

export default withTranslations(ShoppingBag);
